import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Smartphone, X, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";

const APPS = [
  { name: "GPay", scheme: "tez://upi/pay?", cls: "from-blue-600/30 to-blue-400/10" },
  { name: "PhonePe", scheme: "phonepe://pay?", cls: "from-violet-600/30 to-violet-400/10" },
  { name: "Paytm", scheme: "paytmmp://pay?", cls: "from-sky-600/30 to-sky-400/10" },
  { name: "Any UPI app", scheme: "upi://pay?", cls: "from-emerald-600/30 to-emerald-400/10" },
];

export function UpiCheckout({ order, onSubmitted, onClose }) {
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const query = order.upi_uri.split("?")[1];

  const copy = (text, label) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };

  const submit = async () => {
    if (!/^\d{12}$/.test(utr.trim())) return toast.error("UTR must be exactly 12 digits");
    setBusy(true);
    try {
      const r = await api.post(`/billing/orders/${order.order_id}/utr`, { utr: utr.trim() });
      toast.success("UTR submitted — activation after verification");
      onSubmitted(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} data-testid="upi-checkout" className="glass rounded-2xl p-6 relative">
      <button data-testid="upi-checkout-close" onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white"><X size={16} /></button>
      <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 mb-1">UPI Checkout · {order.order_id}</div>
      <div className="font-display text-xl font-bold">{order.pack_name} pack · <span className="text-gradient">₹{order.amount.toLocaleString("en-IN")}</span></div>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 mt-5">
        <div className="flex flex-col items-center">
          <div className="rounded-xl bg-white p-2"><img data-testid="upi-qr" src={order.qr} alt="UPI QR" className="w-44 h-44" /></div>
          <div className="mt-2 text-[10px] font-mono text-slate-500 text-center">Scan with any UPI app</div>
        </div>
        <div className="space-y-4">
          <Step n="1" title="Pay the exact amount to">
            <div className="flex items-center gap-2 mt-1">
              <span data-testid="upi-payee-vpa" className="font-mono text-sm text-white">{order.payee_vpa}</span>
              <button data-testid="copy-vpa" onClick={() => copy(order.payee_vpa, "UPI ID")} className="text-slate-500 hover:text-cyan-300"><Copy size={13} /></button>
              <span className="text-xs text-slate-500">· {order.payee_name}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              {APPS.map((a) => (
                <a key={a.name} href={a.scheme + query} data-testid={`pay-${a.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-center text-xs font-semibold py-2 rounded-lg bg-gradient-to-br ${a.cls} border border-white/10 hover:border-white/30 transition-colors inline-flex items-center justify-center gap-1`}>
                  <Smartphone size={12} /> {a.name}
                </a>
              ))}
            </div>
          </Step>
          <Step n="2" title="Note the 12-digit UTR / transaction ID shown by your UPI app" />
          <Step n="3" title="Submit the UTR — we verify and activate your pack">
            <div className="flex gap-2 mt-2">
              <input data-testid="utr-input" value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, "").slice(0, 12))} placeholder="e.g. 412345678901" inputMode="numeric"
                className="flex-1 bg-[#0A0C14] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-purple-500/40 placeholder:text-slate-600" />
              <button data-testid="utr-submit" onClick={submit} disabled={busy || utr.length !== 12}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 text-white disabled:opacity-40 inline-flex items-center gap-2">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Submit
              </button>
            </div>
          </Step>
        </div>
      </div>
    </motion.div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 text-[10px] font-mono flex items-center justify-center shrink-0">{n}</div>
      <div className="flex-1"><div className="text-sm text-slate-300">{title}</div>{children}</div>
    </div>
  );
}
